import React from 'react';
import { InsightIndexEntry } from '../../types';
import useReveal from '../../hooks/useReveal';
import { BAR_ANIMATION_MS, REVEAL_DELAY_MS, REVEAL_DURATION_MS, toMs } from '../../constants/animation';

interface InsightIndexSectionProps {
  title: string;
  insights: Array<InsightIndexEntry | Record<string, any>>;
  primaryColor?: string;
  accentColor?: string;
  icon?: React.ReactNode;
  subtitle?: string;
  emptyMessage?: string;
}

const DEFAULT_SUBTITLE = 'BASELINE: Online Adult Population | CONTAINS: Engaged Audiences Data';

const parseNumber = (value: any): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const match = value.replace(/,/g, '').match(/-?\d+(\.\d+)?/);
    if (match) {
      const parsed = Number(match[0]);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
};

const normalizeComposition = (value: any): number | null => {
  const parsed = parseNumber(value);
  if (parsed === null) return null;
  if (parsed > 1 && parsed <= 100) return Math.round(parsed * 10) / 10;
  if (parsed >= 0 && parsed <= 1) return Math.round(parsed * 1000) / 10;
  if (parsed > 100) return Math.round(parsed);
  return parsed;
};

const normalizeInsight = (entry: InsightIndexEntry | Record<string, any>) => {
  const base = entry as Record<string, any>;
  const insight =
    base.insight ||
    base.label ||
    base.name ||
    base.title ||
    base.attribute ||
    'Insight';
  const index =
    parseNumber(
      base.index ??
        base.Index ??
        base.value ??
        base.score ??
        base.indicator ??
        base.metric
    ) ?? 0;
  const compCandidate =
    base.composition ??
    base.compositionPercent ??
    base.percent ??
    base.percentage ??
    base['% Composition'] ??
    base['Percent Composition'] ??
    base['composition_percent'] ??
    base.share ??
    base['%'] ??
    null;
  const composition = normalizeComposition(compCandidate);
  const note =
    base.note ||
    base.description ||
    base.subtitle ||
    base['shortDescription'] ||
    '';
  const category = base.category || base.group || base.segment || '';

  return {
    insight: String(insight),
    index,
    composition,
    category: category ? String(category) : undefined,
    note: note ? String(note) : undefined,
  };
};

export default function InsightIndexSection({
  title,
  insights,
  primaryColor = '#0E7490',
  accentColor = '#14B8A6',
  icon,
  subtitle = DEFAULT_SUBTITLE,
  emptyMessage = 'No statistically significant insights were provided for this audience.',
}: InsightIndexSectionProps) {
  const { ref, isVisible } = useReveal<HTMLDivElement>({ threshold: 0.2, once: true });
  const normalized = (Array.isArray(insights) ? insights : [])
    .map(normalizeInsight)
    .filter((item) => item.index !== null && Number.isFinite(item.index))
    .sort((a, b) => (b.index ?? 0) - (a.index ?? 0))
    .slice(0, 20);

  if (normalized.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center mb-4">
          {icon ? <div className="mr-3 text-2xl">{icon}</div> : null}
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
            <p className="text-gray-600">{subtitle}</p>
          </div>
        </div>
        <div className="text-center py-10 text-slate-500">
          <div className="text-4xl mb-3">ℹ️</div>
          <p className="text-sm max-w-md mx-auto leading-relaxed">{emptyMessage}</p>
        </div>
      </div>
    );
  }

  const maxIndex = (() => {
    const highest = Math.max(...normalized.map((item) => item.index || 0));
    const rounded = Math.ceil(highest / 25) * 25;
    return Math.max(125, rounded, 100);
  })();
  const baseline = 100;
  const baselinePosition = Math.min(
    100,
    (baseline / (maxIndex || baseline)) * 100
  );

  const headerIcon =
    icon ??
    (title.toLowerCase().includes('psychological') ? '🧠' : title.toLowerCase().includes('hobbies') ? '🎸' : title.toLowerCase().includes('routine') ? '⏱️' : null);
  void accentColor;
  const baseDelay = isVisible ? REVEAL_DELAY_MS : 0;

  return (
    <div
      ref={ref}
      className="bg-white rounded-lg shadow-md p-6 space-y-6"
      style={{
        transition: `transform ${toMs(REVEAL_DURATION_MS)} ease-out, opacity ${toMs(REVEAL_DURATION_MS)} ease-out`,
        transform: isVisible ? 'translateY(0)' : 'translateY(24px)',
        opacity: isVisible ? 1 : 0.25,
        transitionDelay: toMs(baseDelay),
      }}
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            {headerIcon ? <div className="text-3xl leading-none">{headerIcon}</div> : null}
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
              <p className="text-gray-600 text-sm">{subtitle}</p>
            </div>
          </div>
        </div>
        <div className="text-right text-xs text-slate-500 leading-tight">
          <div>INDEX SCALE TOP: {maxIndex}</div>
          <div>BASELINE: 100</div>
        </div>
      </div>

      <div className="space-y-5">
        {normalized.map((item, idx) => {
          const widthPercent = Math.max(
            0,
            Math.min(100, (item.index / maxIndex) * 100)
          );
          const animatedWidth = isVisible ? widthPercent : 0;
          const rowDelay = isVisible ? REVEAL_DELAY_MS + idx * 140 : 0;
          const baselineLeft = Math.min(100, Math.max(0, baselinePosition));
          const showBaselineMarker = animatedWidth < baselineLeft - 0.5;
          return (
            <div
              key={`${item.insight}-${idx}`}
              className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,2fr)_auto] gap-6 items-center"
              style={{
                transition: `opacity ${toMs(REVEAL_DURATION_MS)} ease-out`,
                opacity: isVisible ? 1 : 0,
                transitionDelay: toMs(rowDelay),
              }}
            >
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate">
                  {item.insight}
                </div>
                {item.note ? (
                  <div className="text-xs text-slate-500">{item.note}</div>
                ) : null}
              </div>

              <div className="relative w-full pb-5">
                <div className="relative h-8 bg-slate-200 rounded-full overflow-hidden">
                  {showBaselineMarker ? (
                    <div
                      className="absolute inset-y-0"
                      style={{
                        left: `${baselineLeft}%`,
                        zIndex: 0,
                      }}
                    >
                      <div className="w-[2px] h-full bg-white/85" />
                    </div>
                  ) : null}
                  <div
                    className="h-full rounded-full flex items-center justify-end pr-3 text-xs font-semibold text-white"
                    style={{
                      width: `${animatedWidth}%`,
                      minWidth: animatedWidth > 0 ? '56px' : '0',
                      backgroundColor: primaryColor,
                      zIndex: 1,
                      transition: `width ${toMs(BAR_ANIMATION_MS)} ease-out`,
                      transitionDelay: toMs(rowDelay),
                    }}
                  >
                      {Math.round(item.index)}
                  </div>
                </div>
                <div
                  className="absolute top-full mt-1 text-[11px] font-semibold text-slate-500"
                  style={{
                    left: `${baselineLeft}%`,
                    transform: 'translateX(-50%)',
                  }}
                >
                  100
                </div>
              </div>

              <div className="text-right">
                <div className="text-[11px] uppercase tracking-wide text-slate-500">
                  Index
                </div>
                <div className="text-xl font-bold text-slate-900">
                  {Math.round(item.index)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="pt-2 text-xs text-slate-500 border-t border-slate-100">
        Index indicates how likely your audience is to possess a trait compared to the baseline audience
        average of 100.
      </div>
    </div>
  );
}
